import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import UserService from '../../../services/UserService';
import AuthService from '../../../services/AuthService';

const UserDetailModal = ({ show, user, onHide, onUpdateUser, onRefreshUsers }) => {
    const [isEditMode, setIsEditMode] = useState(false);
    const [userData, setUserData] = useState({});
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [lightboxImage, setLightboxImage] = useState('');
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [isRejecting, setIsRejecting] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [isLoadingUserDetails, setIsLoadingUserDetails] = useState(false);

    const currentUser = AuthService.getCurrentUser();
    const currentUsername = currentUser ? currentUser.username : null;

    useEffect(() => {
        if (show && user && user.id) {
            loadUserWithIdentification();
        }
    }, [show, user]);

    const loadUserWithIdentification = async () => {
        if (!user || !user.id) return;

        setIsLoadingUserDetails(true);
        try {
            // Charger les données complètes de l'utilisateur avec identification
            const userWithIdentification = await UserService.getUserWithIdentification(user.id);
            setUserData(userWithIdentification);
        } catch (error) {
            console.error('Error loading user details:', error);
            toast.error('Erreur lors du chargement des détails de l\'utilisateur');
            // Fallback: utiliser les données de base si le chargement échoue
            setUserData({ ...user });
        } finally {
            setIsLoadingUserDetails(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUserData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSave = () => {
        if (!userData.id) {
            console.error('userData.id is undefined');
            return;
        }

        const formattedUserData = {
            id: userData.id,
            firstName: userData.firstName,
            lastName: userData.lastName,
            email: userData.email,
            phoneNumber: userData.phoneNumber,
            dateOfBirthString: userData.dateOfBirthString,
            gender: userData.gender,
            profession: userData.profession,
            streetAddress: userData.streetAddress,
            neighborhood: userData.neighborhood,
            city: userData.city,
            province: userData.province,
            country: userData.country,
            username: userData.username,
            additionalInfos: userData.additionalInfos,
            roleNames: userData.roleNames || []
        };

        onUpdateUser(formattedUserData);
        setIsEditMode(false);
    };

    const handleClose = () => {
        onHide();
        setIsEditMode(false);
        setUserData({});
    };

    const openLightbox = (image) => {
        if (!image.startsWith('data:image')) {
            image = `data:image/jpeg;base64,${image}`;
        }

        const img = new Image();
        img.src = image;
        img.onload = () => {
            setLightboxImage(image);
            setIsImageLoaded(true);
            setIsLightboxOpen(true);
        };
        img.onerror = () => {
            console.error('Erreur lors du chargement de l\'image');
        };
    };

    const handleValidation = async (validation) => {
        if (!userData.id) {
            console.error('userData.id is undefined');
            return;
        }

        if (validation) {
            setIsValidating(true);
        } else {
            setIsRejecting(true);
        }

        setLoading(true);
        try {
            const userRequestDTO = {
                id: userData.id,
                username: currentUsername,
                identificationResponseDTO: {
                    ...userData.identificationResponseDTO,
                    rejectReason: rejectReason
                }
            };
            await UserService.validateIdentification(userRequestDTO, validation, 'fr');
            toast.success(`Identification ${validation ? 'validée' : 'rejetée'} avec succès!`);
            handleClose();
            onRefreshUsers();
            setRejectReason('');
        } catch (error) {
            toast.error(`Erreur lors de la ${validation ? 'validation' : 'rejet'} de l'identification.`);
        } finally {
            setLoading(false);
            setIsValidating(false);
            setIsRejecting(false);
            setShowRejectModal(false);
        }
    };

    const handleReject = () => {
        setShowRejectModal(true);
    };

    const handleRejectModalClose = () => {
        setShowRejectModal(false);
    };

    const handleModalClose = () => {
        handleClose();
    };

    if (!user) return null;

    return (
        <>
            <Modal
                show={show}
                onHide={handleClose}
                size="xl"
                backdrop="static"
                keyboard={true}
                style={{ zIndex: 1060 }}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {isEditMode ? 'Édition du Profil' : 'Détails de l\'Utilisateur'}
                        {!isEditMode && (
                            <Button
                                variant="outline-primary"
                                size="sm"
                                className="ms-3"
                                onClick={() => setIsEditMode(true)}
                            >
                                <i className="fas fa-edit me-2"></i>Modifier
                            </Button>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isLoadingUserDetails ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <p className="mt-2">Chargement des détails de l'utilisateur...</p>
                        </div>
                    ) : (
                        <Form>
                            <h5 className="mb-3">Informations personnelles</h5>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Prénom</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="firstName"
                                            value={userData.firstName || ''}
                                            onChange={handleChange}
                                            readOnly={!isEditMode}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nom</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="lastName"
                                            value={userData.lastName || ''}
                                            onChange={handleChange}
                                            readOnly={!isEditMode}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date de naissance</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="dateOfBirthString"
                                            value={userData.dateOfBirthString || ''}
                                            onChange={handleChange}
                                            readOnly={!isEditMode}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Genre</Form.Label>
                                        <Form.Select
                                            name="gender"
                                            value={userData.gender || ''}
                                            onChange={handleChange}
                                            disabled={!isEditMode}
                                        >
                                            <option value="MALE">Homme</option>
                                            <option value="FEMALE">Femme</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Profession</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="profession"
                                            value={userData.profession || ''}
                                            onChange={handleChange}
                                            readOnly={!isEditMode}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nom d'utilisateur</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="username"
                                            value={userData.username || ''}
                                            onChange={handleChange}
                                            readOnly={!isEditMode}
                                            disabled
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <h5 className="mb-3 mt-4">Coordonnées</h5>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="email"
                                            value={userData.email || ''}
                                            onChange={handleChange}
                                            readOnly={!isEditMode}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Téléphone</Form.Label>
                                        <Form.Control
                                            type="tel"
                                            name="phoneNumber"
                                            value={userData.phoneNumber || ''}
                                            onChange={handleChange}
                                            readOnly={!isEditMode}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <h5 className="mb-3 mt-4">Adresse</h5>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Pays</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="country"
                                            value={userData.country || ''}
                                            onChange={handleChange}
                                            readOnly={!isEditMode}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Province</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="province"
                                            value={userData.province || ''}
                                            onChange={handleChange}
                                            readOnly={!isEditMode}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Ville</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="city"
                                            value={userData.city || ''}
                                            onChange={handleChange}
                                            readOnly={!isEditMode}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Rue</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="streetAddress"
                                            value={userData.streetAddress || ''}
                                            onChange={handleChange}
                                            readOnly={!isEditMode}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <h5 className="mb-3 mt-4">Informations supplémentaires</h5>
                            <Row>
                                <Col md={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Notes additionnelles</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            name="additionalInfos"
                                            value={userData.additionalInfos || ''}
                                            onChange={handleChange}
                                            readOnly={!isEditMode}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>























                            {!isEditMode && userData.identificationResponseDTO && (
                                <div>
                                    <h5 className="mb-3 mt-4">Photos selfies de face</h5>
                                    <Row>
                                        {userData.identificationResponseDTO.selfieImageFront && (
                                            <Col md={12}>
                                                <div className="mb-3" style={{ cursor: 'pointer' }}>
                                                    <img
                                                        src={userData.identificationResponseDTO.selfieImageFront}
                                                        alt={'Selfie Front'}
                                                        className="img-fluid cursor-pointer"
                                                        style={{ maxWidth: '300px', maxHeight: '200px', objectFit: 'contain', borderRadius: '5%' }}
                                                        onClick={() => openLightbox(userData.identificationResponseDTO.selfieImageFront)}
                                                    />
                                                </div>
                                            </Col>
                                        )}
                                    </Row>

                                    <div>
                                        {userData.identificationResponseDTO.idDocuments && userData.identificationResponseDTO.idDocuments.map((doc, index) => (
                                            <div key={index}>
                                                <h5 className="mb-3 mt-4">Documents</h5>
                                                <div className="border p-3 mb-3">
                                                    <Row>
                                                        <Col md={6}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>Type de document</Form.Label>
                                                                <Form.Control
                                                                    type="text"
                                                                    value={doc.idType || ''}
                                                                    readOnly
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={6}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>Pays d'émission</Form.Label>
                                                                <Form.Control
                                                                    type="text"
                                                                    value={doc.countryOfIssue || ''}
                                                                    readOnly
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col md={6}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>Date de délivrance</Form.Label>
                                                                <Form.Control
                                                                    type="text"
                                                                    value={(doc.deliveryDate && new Date(doc.deliveryDate).toLocaleDateString()) || ''}
                                                                    readOnly
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={6}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>Date d'expiration</Form.Label>
                                                                <Form.Control
                                                                    type="text"
                                                                    value={(doc.expiryDate && new Date(doc.expiryDate).toLocaleDateString()) || ''}
                                                                    readOnly
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col md={12}>
                                                            <Form.Group className="mb-3" style={{ cursor: 'pointer' }}>
                                                                <Form.Label>Image du document</Form.Label> <br />
                                                                {doc.documentImage ? (
                                                                    <img
                                                                        src={doc.documentImage}
                                                                        alt={doc.documentName || 'Document'}
                                                                        className="img-fluid cursor-pointer"
                                                                        style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }}
                                                                        onClick={() => openLightbox(doc.documentImage)}
                                                                    />
                                                                ) : (
                                                                    <p>Aucune image disponible</p>
                                                                )}
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {userData.identificationResponseDTO.rejectReason && (
                                        <Row className="m-3" style={{width: '97%', border: '2px dashed rgb(133 121 121)',}}>
                                            <h3 className="mt-3">Motif(s) de Rejet</h3>
                                            <ul style={{width: '93%', marginLeft: '15px'}}>
                                                {userData.identificationResponseDTO.rejectReason.split(';').map((message, index) => (
                                                    <li style={{marginTop: '10px'}} key={index}>{message.trim()}</li>
                                                ))}
                                            </ul>
                                        </Row>
                                    )}

                                    {!userData.idDocsVerified && (
                                        <div className="mt-4 d-flex justify-content-end">
                                            <Button
                                                variant="danger"
                                                className="me-2"
                                                onClick={handleReject}
                                                disabled={loading || isValidating}
                                            >
                                                {isRejecting ? (
                                                    <span>
                                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                        Rejet en cours...
                                                    </span>
                                                ) : (
                                                    'Rejeter l\'identification'
                                                )}
                                            </Button>
                                            {!isValidating && (
                                                <Button
                                                    variant="success"
                                                    onClick={() => handleValidation(true)}
                                                    disabled={loading || isRejecting}
                                                >
                                                    Valider l'identification
                                                </Button>
                                            )}
                                            {isValidating && (
                                                <Button
                                                    variant="success"
                                                    disabled
                                                >
                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                    Validation en cours...
                                                </Button>
                                            )}
                                        </div>
                                    )}

                                    {userData.idDocsVerified && (
                                        <div className="text-center" style={{ color: 'rgb(239 239 223)', backgroundColor: 'rgb(8 73 185 / 68%)', borderRadius: '50%', padding: '10px', fontWeight: 'bold', fontSize: '15px', }}>
                                            Cet utilisateur est déjà identifié.
                                        </div>
                                    )}

                                    {!isEditMode && (
                                        <div className="text-center mt-5" style={{ borderTop: '2px dashed rgb(133 121 121)' }}>
                                            <Button variant="secondary" onClick={handleModalClose} className="mt-3">
                                                Fermer
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {!userData.identificationResponseDTO && (
                                <>
                                    <div className="text-center mt-5" style={{ borderTop: '2px dashed rgb(133, 121, 121)' }}>
                                        <div className="text-center mt-3" style={{
                                            color: 'rgb(219 215 12)',
                                            backgroundColor: 'rgb(0 128 8 / 68%)',
                                            borderRadius: '50%',
                                            padding: '10px',
                                            fontWeight: 'bold',
                                            fontSize: '15px',
                                        }}>
                                            Aucune identification soumise.
                                        </div>
                                    </div>

                                    <div className="text-center mt-5" style={{ borderTop: '2px dashed rgb(133 121 121)' }}>
                                        <Button variant="secondary" onClick={handleModalClose} className="mt-3">
                                            Fermer
                                        </Button>
                                    </div>
                                </>
                            )}
                        </Form>
                    )}

                    {isEditMode && (
                        <div className="mt-4 d-flex justify-content-end">
                            <Button
                                variant="secondary"
                                className="me-2"
                                onClick={() => setIsEditMode(false)}
                            >
                                Annuler
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSave}
                            >
                                Sauvegarder
                            </Button>
                        </div>
                    )}
                </Modal.Body>
            </Modal>

            {isLightboxOpen && isImageLoaded && (
                <Lightbox
                    mainSrc={lightboxImage}
                    onCloseRequest={() => {
                        setIsLightboxOpen(false);
                        setIsImageLoaded(false);
                    }}
                    reactModalStyle={{ overlay: { zIndex: 10055 } }}
                    imageLoadErrorMessage="Erreur lors du chargement de l'image"
                />
            )}

            <Modal show={showRejectModal} onHide={handleRejectModalClose}
                   style={{
                       position: 'fixed',
                       top: 0,
                       left: 0,
                       right: 0,
                       bottom: 0,
                       backgroundColor: 'rgba(0, 0, 0, 0.2)',
                       backdropFilter: 'blur(5px)',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       zIndex: '2000',
                   }}>
                <Modal.Header closeButton>
                    <Modal.Title>Motif de rejet</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Veuillez saisir le motif du rejet :</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    {!isRejecting && (
                        <Button variant="secondary" onClick={handleRejectModalClose}>
                            Annuler
                        </Button>
                    )}
                    <Button variant="danger" onClick={() => handleValidation(false)} disabled={isRejecting}>
                        {isRejecting ? (
                            <span>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                Rejet en cours...
                            </span>
                        ) : (
                            'Valider le rejet'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default UserDetailModal;